import dayjs from "dayjs";
import "dayjs/locale/ja";
import customParseFormat from "dayjs/plugin/customParseFormat";
import dayOfYear from "dayjs/plugin/dayOfYear";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

import { App } from "./App";
import type { ImageZipRequest, ImageZipResult } from "./App/ImageZipExporter";
import type { RenderStatus } from "./App/RenderCoordinator";
import type { VideoRequest, VideoResult } from "./App/VideoRecorder";

declare const __COMMIT_HASH__: string;
declare const __COMMIT_DATE__: string;
declare global {
    interface Window {
        __COMMIT_HASH__: string;
        __COMMIT_DATE__: string;
        setParams: (inputParams: {
            [key: string]: string;
        }) => Promise<RenderStatus>;
        changeParams: (inputParams: {
            [key: string]: string;
        }) => Promise<RenderStatus>;
        toDataURL: () => Promise<string>;
        renderVideo: (request?: VideoRequest) => Promise<VideoResult>;
        abortVideo: () => void;
        renderImageZip: (request?: ImageZipRequest) => Promise<ImageZipResult>;
        abortImageZip: () => void;
        waitForRender: (timeoutMs?: number) => Promise<RenderStatus>;
        getRenderStatus: () => RenderStatus;
        readonly renderReady: boolean;
        ready: {
            readonly map: number;
            readonly images: number;
            readonly contour: number;
            get_status: () => boolean;
        };
    }
}

const readyP = new Promise<void>((resolve) =>
    document.readyState === "loading"
        ? document.addEventListener("DOMContentLoaded", () => resolve())
        : resolve(),
);

function setupDayjs() {
    dayjs.locale("ja");
    dayjs.extend(customParseFormat);
    dayjs.extend(dayOfYear);
    dayjs.extend(isSameOrAfter);
    dayjs.extend(isSameOrBefore);
}

(async () => {
    window.__COMMIT_HASH__ = __COMMIT_HASH__;
    window.__COMMIT_DATE__ = __COMMIT_DATE__;
    setupDayjs();
    await App.fetch();
    await readyP;
    const app = new App();
    window.setParams = (inputParams) => app.setParams(inputParams);
    window.changeParams = (inputParams) => app.changeParams(inputParams);
    window.toDataURL = () => app.toDataURL();
    window.renderVideo = (request) => app.renderVideo(request ?? {});
    window.abortVideo = () => app.abortVideo();
    window.renderImageZip = (request) => app.renderImageZip(request ?? {});
    window.abortImageZip = () => app.abortImageZip();
    window.waitForRender = (timeoutMs) => app.waitForRender(timeoutMs);
    window.getRenderStatus = () => app.getRenderStatus();
    Object.defineProperty(window, "renderReady", {
        get: () => app.getRenderStatus().ready,
    });
    window.ready = Object.defineProperties(
        { get_status: () => app.getRenderStatus().ready },
        {
            map: { get: () => 1 },
            images: {
                get: () =>
                    app
                        .getRenderStatus()
                        .pending.some((part) =>
                            ["app", "shade", "export"].includes(part),
                        )
                        ? 0
                        : 1,
            },
            contour: {
                get: () =>
                    app
                        .getRenderStatus()
                        .pending.some((part) =>
                            ["app", "contour"].includes(part),
                        )
                        ? 0
                        : 1,
            },
        },
    ) as Window["ready"];
    await app.init();
})();
